//
// # Render Content Base
//
// The environment-neutral render core shared by every content type: only what
// the draw path reads/writes and what the entity tree requires of its root.
// Every member here is load-bearing — see the per-field notes. Selection,
// change-log, modes, identity, and all other interaction live in `ContentBase`
// (see `base.ts`), which extends this.
//
// Typed against `RenderHost` (not the fat `InteractionHost`) so the draw path
// carries no dependency on the interaction layer.
//

import { model } from "GenAlphaModel";
import type { schema } from "GenAlphaSchema";
import type { RedrawRequest } from "../redraw";
import type { RenderHost } from "../render_host";
import { DrawTo } from "../draw";
import * as tree from "../tree";
import { EntityKind } from "../entity";
import { ContentKind } from "./kind";
export { ContentKind };

// Interface maintained by everything that can be a "top-level child" of the
// Content. Basically all the stuff the `Content` base-class uses and calls.
export interface ChildInterface extends tree.Child {
  readonly entityKind: EntityKind;
  highlight: (state: boolean) => RedrawRequest;
  invalidate: () => RedrawRequest;
}

// # Render Content Base Class
//
// The render-only surface of a content object.
//
export abstract class RenderContentBase {
  constructor(readonly editor: RenderHost) {}

  // `tree.Parent` conformance: the content is the root of the entity tree.
  // `tree/entity.ts` reads `parent.xform`/`parent.highlighted` and registers in
  // `parent.children`. (`root` is provided by the concrete content classes,
  // which narrow it to their own type — the render base is not itself a
  // `Parent`.)
  readonly parent = null;
  readonly highlighted = false;
  readonly xform = model.Xform.default(); // Root content's transform is identity.
  readonly children = new Set<tree.Child>();
  // Under composition the render object IS the entity-tree root; concrete render
  // classes narrow the return type to themselves.
  get root(): this {
    return this;
  }

  // Written by `recalcBbox`, read by the renderer to fit the view.
  public bbox: model.Bbox = model.Bbox.empty();

  // PDK for resolving layer colors. Only layout content populates it (schematic/
  // symbol have no layers); exposed here so tree entities that resolve colors at
  // construction (e.g. routes) can read it via `root.pdk`.
  pdk: schema.pdk.PDK | null = null;

  // Abstract render surface.
  abstract readonly contentKind: ContentKind; // Renderer dispatches on this.
  abstract redraw(to: DrawTo): unknown;
  abstract recalcBbox(): void;

  // Entities invoke this via `root.requestRedraw()`.
  requestRedraw = () => this.editor.requestRedraw();
}
